import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Search, Filter, X, ChevronDown, Plus, Coins, Zap, Target, Upload, FileText, Image, AlertTriangle, RefreshCw, ChevronRight, BookOpen, Infinity } from 'lucide-react';
import { useAuth } from '../contexts/AuthContextSupabase';
import { matchCreators, MatchResult, QueryAnalysis } from '../services/aiMatching';
import { extractBriefFromFile, matchCreatorsWithBrief, BriefJson, EMPTY_BRIEF, BriefMatchResponse } from '../services/briefExtraction';
import AddToCastingModal from '../components/AddToCastingModal';
import { supabase } from '../lib/supabase';

const MatchingIA: React.FC = () => {
  const { userProfile } = useAuth();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [queryAnalysis, setQueryAnalysis] = useState<QueryAnalysis | null>(null);
  const [results, setResults] = useState<MatchResult[]>([]);
  const [filteredResults, setFilteredResults] = useState<MatchResult[]>([]);
  const [excludedByAI, setExcludedByAI] = useState<MatchResult[]>([]);
  const [showExcluded, setShowExcluded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditsBalance, setCreditsBalance] = useState<number>(0);
  const [isLoadingCredits, setIsLoadingCredits] = useState(true);

  // Filtres locaux
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    minFollowers: 0,
    maxFollowers: 10000000,
    minEngagement: 0,
    minScore: 0,
    category: '',
  });

  // Modal casting
  const [isCastingModalOpen, setIsCastingModalOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<MatchResult | null>(null);

  // ── Casting mode ──────────────────────────────────────────────────────────
  type CastingMode = 'brief';
  const [castingMode, setCastingMode] = useState<CastingMode>('brief');

  // Brief upload / paste
  const [briefFile, setBriefFile] = useState<File | null>(null);
  const [briefPastedText, setBriefPastedText] = useState('');
  const [isExtractingBrief, setIsExtractingBrief] = useState(false);
  const [extractionError, setExtractionError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Extracted + editable brief
  const [extractedBrief, setExtractedBrief] = useState<BriefJson | null>(null);

  // Brief matching results
  const [briefResults, setBriefResults] = useState<BriefMatchResponse | null>(null);
  const [isBriefMatching, setIsBriefMatching] = useState(false);
  const [briefError, setBriefError] = useState<string | null>(null);

  // Brief mode sections toggle
  const [showDirectSection, setShowDirectSection] = useState(true);
  const [showLateralSection, setShowLateralSection] = useState(true);
  const [showDiscoverySection, setShowDiscoverySection] = useState(true);
  const [showBriefExcluded, setShowBriefExcluded] = useState(false);

  // Brand memory: last brief per brand
  const [lastBriefByBrand, setLastBriefByBrand] = useState<Record<string, BriefJson>>({});
  const [showReusePrompt, setShowReusePrompt] = useState(false);
  const [reuseCandidate, setReuseCandidate] = useState<{ brand: string; brief: BriefJson } | null>(null);

  useEffect(() => {
    if (userProfile?.agency_id) {
      loadCredits();
    }
  }, [userProfile?.agency_id]);

  const loadCredits = async () => {
    if (!userProfile?.agency_id) return;

    try {
      const { data, error } = await supabase.rpc('get_organization_credits', {
        org_id: userProfile.agency_id,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setCreditsBalance(data[0].balance);
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setIsLoadingCredits(false);
    }
  };

  // Load brand memory from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('campaly_brief_memory');
      if (stored) setLastBriefByBrand(JSON.parse(stored));
    } catch { /* ignore */ }
  }, []);

  const saveBriefToMemory = (brief: BriefJson) => {
    if (!brief.brand_name) return;
    const updated = { ...lastBriefByBrand, [brief.brand_name.toLowerCase().trim()]: brief };
    setLastBriefByBrand(updated);
    try { localStorage.setItem('campaly_brief_memory', JSON.stringify(updated)); } catch { /* ignore */ }
  };

  const handleFileSelect = (file: File) => {
    const allowed = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setExtractionError('Format non supporté. Utilisez un PDF ou une image (PNG, JPG, WEBP).');
      return;
    }
    setBriefFile(file);
    setExtractionError(null);
  };

  const handleExtractBrief = async () => {
    if (!briefFile && !briefPastedText.trim()) {
      setExtractionError('Veuillez coller le brief ou uploader un fichier.');
      return;
    }
    setIsExtractingBrief(true);
    setExtractionError(null);
    try {
      const brief = await extractBriefFromFile(briefFile!, briefPastedText);
      setExtractedBrief(brief);
      // Check brand memory
      if (brief.brand_name) {
        const key = brief.brand_name.toLowerCase().trim();
        const existing = lastBriefByBrand[key];
        if (existing) {
          setReuseCandidate({ brand: brief.brand_name, brief: existing });
          setShowReusePrompt(true);
        }
      }
    } catch (err) {
      setExtractionError(err instanceof Error ? err.message : 'Extraction échouée');
      setExtractedBrief({ ...EMPTY_BRIEF });
    } finally {
      setIsExtractingBrief(false);
    }
  };

  const handleLaunchBriefMatching = async () => {
    if (!extractedBrief) return;
    setIsBriefMatching(true);
    setBriefError(null);
    setBriefResults(null);
    try {
      const result = await matchCreatorsWithBrief(extractedBrief);
      console.log('[BRIEF-MATCHING] Result:', JSON.stringify({ direct: result.sections?.direct?.length, lateral: result.sections?.lateral?.length, discovery: result.sections?.discovery?.length, stats: result.stats, error: (result as any).error }));
      setBriefResults(result);
      saveBriefToMemory(extractedBrief);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur lors du matching';
      console.error('[BRIEF-MATCHING] Error:', msg, err);
      setBriefError(msg);
    } finally {
      setIsBriefMatching(false);
    }
  };

  const handleAnalyze = async () => {
    if (!query.trim()) {
      setError('Veuillez entrer une requête');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await matchCreators(query);
      setQueryAnalysis(response.query_analysis);
      setResults(response.results);
      setFilteredResults(response.results);
      setExcludedByAI(response.excluded_by_ai || []);
      console.log(`[MATCHING-IA] ${response.results.length} selected, ${response.excluded_by_ai?.length || 0} excluded by AI`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'analyse');
      console.error('[MATCHING-IA] Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...results];

    if (filters.minFollowers > 0) {
      filtered = filtered.filter(r => r.followers_count >= filters.minFollowers);
    }

    if (filters.maxFollowers < 10000000) {
      filtered = filtered.filter(r => r.followers_count <= filters.maxFollowers);
    }

    if (filters.minEngagement > 0) {
      filtered = filtered.filter(r => (r.engagement_rate || 0) >= filters.minEngagement);
    }

    if (filters.minScore > 0) {
      filtered = filtered.filter(r => r.score >= filters.minScore);
    }

    if (filters.category) {
      filtered = filtered.filter(r => r.category === filters.category);
    }

    setFilteredResults(filtered);
  };

  React.useEffect(() => {
    applyFilters();
  }, [filters, results]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'bg-green-500/20 text-green-300 border-green-500/30';
    if (score >= 60) return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    if (score >= 40) return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    return 'bg-white/[0.08] text-white/60 border-white/[0.08]';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Bon Match';
    if (score >= 40) return 'Match Moyen';
    return 'Faible Match';
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Matching IA</h1>
                <p className="text-white/50">Trouvez les créateurs parfaits en langage naturel</p>
              </div>
            </div>
            <div className="flex items-center gap-3 px-5 py-3 bg-white/[0.06] border border-white/[0.08] rounded-xl">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-white/[0.08] flex items-center justify-center">
                  <Coins className="w-5 h-5 text-white/60" />
                </div>
                <div className="text-left">
                  <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Crédits</p>
                  {isLoadingCredits ? (
                    <p className="text-lg font-bold text-white">...</p>
                  ) : (
                    <p className="text-lg font-bold text-white flex items-center justify-center">{creditsBalance >= 999999 ? <Infinity className="w-5 h-5" /> : creditsBalance}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* ── BRIEF MODE ─────────────────────────────────────────────────────── */}
        {castingMode === 'brief' && (
          <div className="space-y-5 mb-6">

            {/* Brand memory reuse prompt */}
            {showReusePrompt && reuseCandidate && (
              <div className="bg-white/[0.06] border border-white/[0.08] rounded-2xl p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-white/60 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-white">Brief précédent trouvé pour {reuseCandidate.brand}</div>
                    <div className="text-xs text-white/50">Voulez-vous réutiliser le dernier brief de {reuseCandidate.brand} ?</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setExtractedBrief(reuseCandidate.brief); setShowReusePrompt(false); }} className="px-3 py-1.5 bg-white text-black text-xs font-semibold rounded-xl hover:bg-white/90 transition-colors">
                    Réutiliser
                  </button>
                  <button onClick={() => setShowReusePrompt(false)} className="px-3 py-1.5 bg-white/[0.08] text-white/60 text-xs font-semibold rounded-xl border border-white/[0.08] hover:bg-white/[0.14] transition-colors">
                    Ignorer
                  </button>
                </div>
              </div>
            )}

            {/* Upload + paste zone — only if no extracted brief yet */}
            {!extractedBrief && (
              <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6">
                <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-white/60" />
                  Importer votre brief
                </h3>

                {/* Drop zone */}
                <div
                  className="border-2 border-dashed border-white/[0.08] rounded-xl p-8 text-center cursor-pointer hover:border-white/20 hover:bg-white/[0.04] transition-all mb-4"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
                  />
                  {briefFile ? (
                    <div className="flex items-center justify-center gap-3">
                      {briefFile.type === 'application/pdf' ? <FileText className="w-8 h-8 text-red-400" /> : <Image className="w-8 h-8 text-white/60" />}
                      <div className="text-left">
                        <div className="text-sm font-semibold text-white">{briefFile.name}</div>
                        <div className="text-xs text-white/50">{(briefFile.size / 1024).toFixed(0)} KB</div>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setBriefFile(null); }} className="ml-2 p-1 rounded-full hover:bg-white/[0.08]">
                        <X className="w-4 h-4 text-white/50" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-10 h-10 text-white/30 mx-auto" />
                      <div className="text-sm font-medium text-white/60">Glisse ton brief ici</div>
                      <div className="text-xs text-white/50">PDF ou image (PNG, JPG, WEBP)</div>
                      <div className="text-xs text-white/30">DOCX : copiez-collez le contenu dans la zone ci-dessous</div>
                    </div>
                  )}
                </div>

                {/* OR separator */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-white/[0.08]" />
                  <span className="text-xs text-white/40 font-medium">OU</span>
                  <div className="flex-1 h-px bg-white/[0.08]" />
                </div>

                {/* Paste zone */}
                <textarea
                  value={briefPastedText}
                  onChange={(e) => setBriefPastedText(e.target.value)}
                  placeholder="Colle directement le brief ici (mail reçu, Google Doc, etc.)..."
                  rows={6}
                  className="w-full px-4 py-3 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm transition-all resize-none"
                />

                {extractionError && (
                  <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2 text-sm text-red-400">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {extractionError}
                    {extractionError.includes('Extraction') && (
                      <button onClick={() => setExtractedBrief({ ...EMPTY_BRIEF })} className="ml-auto text-xs underline">
                        Remplir manuellement
                      </button>
                    )}
                  </div>
                )}

                <button
                  onClick={handleExtractBrief}
                  disabled={isExtractingBrief || (!briefFile && !briefPastedText.trim())}
                  className="mt-4 w-full py-4 bg-white text-black rounded-xl font-semibold hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {isExtractingBrief ? (
                    <><div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />Analyse du brief...</>
                  ) : (
                    <><Sparkles className="w-5 h-5" />Analyser le brief</>
                  )}
                </button>
              </div>
            )}

            {/* Extracted brief recap (editable) */}
            {extractedBrief && !briefResults && (
              <BriefRecapCard
                brief={extractedBrief}
                onChange={setExtractedBrief}
                onReset={() => { setExtractedBrief(null); setBriefResults(null); }}
                onLaunch={handleLaunchBriefMatching}
                isLoading={isBriefMatching}
                error={briefError}
              />
            )}

            {/* Brief matching results */}
            {briefResults && (
              <BriefResultsSections
                response={briefResults}
                showDirect={showDirectSection}
                setShowDirect={setShowDirectSection}
                showLateral={showLateralSection}
                setShowLateral={setShowLateralSection}
                showDiscovery={showDiscoverySection}
                setShowDiscovery={setShowDiscoverySection}
                showExcluded={showBriefExcluded}
                setShowExcluded={setShowBriefExcluded}
                onAddToCasting={(creator) => { setSelectedCreator(creator); setIsCastingModalOpen(true); }}
                onReset={() => { setBriefResults(null); setExtractedBrief(null); setBriefFile(null); setBriefPastedText(''); }}
                getScoreColor={getScoreColor}
                getScoreLabel={getScoreLabel}
              />
            )}
          </div>
        )}

        {/* Query Analysis */}
        {queryAnalysis && (
          <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6 mb-6">
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-white/60" />
              Analyse de votre requête
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {queryAnalysis.brand_name && (
                <div>
                  <div className="text-sm text-white/50">Marque</div>
                  <div className="font-medium text-white">{queryAnalysis.brand_name}</div>
                </div>
              )}
              {queryAnalysis.sector && (
                <div>
                  <div className="text-sm text-white/50">Secteur</div>
                  <div className="font-medium text-white">{queryAnalysis.sector}</div>
                </div>
              )}
              {queryAnalysis.price_segment && (
                <div>
                  <div className="text-sm text-white/50">Positionnement</div>
                  <div className="font-medium text-white">{queryAnalysis.price_segment}</div>
                </div>
              )}
              {queryAnalysis.target_audience && (
                <div className="md:col-span-2">
                  <div className="text-sm text-white/50">Audience cible</div>
                  <div className="font-medium text-white">{queryAnalysis.target_audience}</div>
                </div>
              )}
              {queryAnalysis.brand_persona_description && (
                <div className="md:col-span-2">
                  <div className="text-sm text-white/50">Persona</div>
                  <div className="font-medium text-white">{queryAnalysis.brand_persona_description}</div>
                </div>
              )}
            </div>

            {/* Brand Universe */}
            {queryAnalysis.brand_universe && (
              <div className="mt-4 pt-4 border-t border-white/[0.06]">
                <div className="text-sm font-medium text-white/60 mb-3">Brand Universe ({
                  (queryAnalysis.brand_universe.direct_competitors?.length || 0) +
                  (queryAnalysis.brand_universe.indirect_competitors?.length || 0) +
                  (queryAnalysis.brand_universe.sector_brands?.length || 0) +
                  (queryAnalysis.brand_universe.consumer_overlap_brands?.length || 0) +
                  (queryAnalysis.brand_universe.influencer_ecosystem_brands?.length || 0)
                } marques)</div>
                <div className="space-y-2 text-xs">
                  {queryAnalysis.brand_universe.direct_competitors?.length > 0 && (
                    <div>
                      <span className="font-medium text-blue-400">Concurrents directs ({queryAnalysis.brand_universe.direct_competitors.length}):</span>
                      <span className="text-white/50 ml-2">{queryAnalysis.brand_universe.direct_competitors.slice(0, 8).join(', ')}{queryAnalysis.brand_universe.direct_competitors.length > 8 ? '...' : ''}</span>
                    </div>
                  )}
                  {queryAnalysis.brand_universe.indirect_competitors?.length > 0 && (
                    <div>
                      <span className="font-medium text-green-400">Concurrents indirects ({queryAnalysis.brand_universe.indirect_competitors.length}):</span>
                      <span className="text-white/50 ml-2">{queryAnalysis.brand_universe.indirect_competitors.slice(0, 8).join(', ')}{queryAnalysis.brand_universe.indirect_competitors.length > 8 ? '...' : ''}</span>
                    </div>
                  )}
                  {queryAnalysis.brand_universe.sector_brands?.length > 0 && (
                    <div>
                      <span className="font-medium text-purple-400">Secteur élargi ({queryAnalysis.brand_universe.sector_brands.length}):</span>
                      <span className="text-white/50 ml-2">{queryAnalysis.brand_universe.sector_brands.slice(0, 8).join(', ')}{queryAnalysis.brand_universe.sector_brands.length > 8 ? '...' : ''}</span>
                    </div>
                  )}
                  {queryAnalysis.brand_universe.consumer_overlap_brands?.length > 0 && (
                    <div>
                      <span className="font-medium text-orange-400">Même consommateurs ({queryAnalysis.brand_universe.consumer_overlap_brands.length}):</span>
                      <span className="text-white/50 ml-2">{queryAnalysis.brand_universe.consumer_overlap_brands.slice(0, 8).join(', ')}{queryAnalysis.brand_universe.consumer_overlap_brands.length > 8 ? '...' : ''}</span>
                    </div>
                  )}
                  {queryAnalysis.brand_universe.influencer_ecosystem_brands?.length > 0 && (
                    <div>
                      <span className="font-medium text-pink-400">Écosystème influenceur ({queryAnalysis.brand_universe.influencer_ecosystem_brands.length}):</span>
                      <span className="text-white/50 ml-2">{queryAnalysis.brand_universe.influencer_ecosystem_brands.slice(0, 8).join(', ')}{queryAnalysis.brand_universe.influencer_ecosystem_brands.length > 8 ? '...' : ''}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        {results.length > 0 && (
          <div className="mb-6">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white/[0.08] text-white/60 rounded-xl border border-white/[0.08] hover:bg-white/[0.14] transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filtres
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            {showFilters && (
              <div className="mt-4 bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                      Followers min
                    </label>
                    <input
                      type="number"
                      value={filters.minFollowers}
                      onChange={(e) => setFilters({ ...filters, minFollowers: Number(e.target.value) })}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                      Followers max
                    </label>
                    <input
                      type="number"
                      value={filters.maxFollowers}
                      onChange={(e) => setFilters({ ...filters, maxFollowers: Number(e.target.value) })}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                      Engagement min (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={filters.minEngagement}
                      onChange={(e) => setFilters({ ...filters, minEngagement: Number(e.target.value) })}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">
                      Score minimum
                    </label>
                    <input
                      type="number"
                      value={filters.minScore}
                      onChange={(e) => setFilters({ ...filters, minScore: Number(e.target.value) })}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {filteredResults.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">
                {filteredResults.length} résultat{filteredResults.length > 1 ? 's' : ''}
              </h3>
            </div>

            <div className="space-y-4">
              {filteredResults.map((result) => (
                <div
                  key={result.influencer_id}
                  className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6 hover:bg-white/[0.06] transition-colors"
                >
                  <div className="flex items-start gap-6">
                    {/* Avatar */}
                    <img
                      src={result.profile_image_url || `https://ui-avatars.com/api/?name=${result.username}&size=80`}
                      alt={result.username}
                      className="w-20 h-20 rounded-xl object-cover"
                    />

                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-lg font-bold text-white">
                            {result.display_name || result.username}
                          </h4>
                          <p className="text-white/50">@{result.username}</p>
                        </div>

                        {/* Score Badge */}
                        <div className={`px-4 py-2 rounded-xl border-2 ${getScoreColor(result.score)}`}>
                          <div className="text-2xl font-bold">{result.score}</div>
                          <div className="text-xs">{getScoreLabel(result.score)}</div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-6 mb-4 text-sm text-white/50">
                        <div>
                          <span className="font-semibold text-white">
                            {result.followers_count.toLocaleString()}
                          </span>{' '}
                          followers
                        </div>
                        {result.engagement_rate && (
                          <div>
                            <span className="font-semibold text-white">
                              {result.engagement_rate.toFixed(2)}%
                            </span>{' '}
                            engagement
                          </div>
                        )}
                        {result.category && (
                          <div className="px-3 py-1 bg-white/[0.08] rounded-full text-white/60">
                            {result.category}
                          </div>
                        )}
                      </div>

                      {/* Match Level & Universe Level */}
                      <div className="flex items-center gap-3 mb-4">
                        {result.match_level && (
                          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            result.match_level === 'EXCEPTIONAL' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                            result.match_level === 'STRONG' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                            result.match_level === 'MEDIUM' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                            'bg-white/[0.08] text-white/60 border border-white/[0.08]'
                          }`}>
                            {result.match_level === 'EXCEPTIONAL' ? '⭐ Match Exceptionnel' :
                             result.match_level === 'STRONG' ? '🔥 Match Fort' :
                             result.match_level === 'MEDIUM' ? '✓ Match Moyen' : 'Match Faible'}
                          </div>
                        )}
                        {result.brand_universe_level && result.brand_universe_level !== 'NONE' && (
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            result.brand_universe_level === 'EXACT' ? 'bg-purple-500/20 text-purple-300' :
                            result.brand_universe_level === 'DIRECT' ? 'bg-blue-500/20 text-blue-300' :
                            'bg-green-500/20 text-green-300'
                          }`}>
                            {result.brand_universe_level === 'EXACT' ? '🎯 Même marque' :
                             result.brand_universe_level === 'DIRECT' ? '🔵 Concurrent direct' :
                             '🟢 Concurrent indirect'}
                          </div>
                        )}
                      </div>

                      {/* Matched Brands */}
                      {result.matched_brands && result.matched_brands.length > 0 && (
                        <div className="mb-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                          <div className="text-xs font-semibold text-blue-300 mb-1">
                            💼 Marques travaillées ({result.matched_brands.length})
                          </div>
                          <div className="text-xs text-blue-300/70">
                            {result.matched_brands.slice(0, 8).join(', ')}
                            {result.matched_brands.length > 8 && ` +${result.matched_brands.length - 8}`}
                          </div>
                        </div>
                      )}

                      {/* Enrichment & Creator Profile Data */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {result.enrichment?.language && (
                          <div className="p-2 bg-white/[0.06] rounded-lg">
                            <div className="text-xs text-white/40">Langue</div>
                            <div className="text-sm font-medium text-white">{result.enrichment.language}</div>
                          </div>
                        )}
                        {result.enrichment?.content_style && (
                          <div className="p-2 bg-white/[0.06] rounded-lg">
                            <div className="text-xs text-white/40">Style</div>
                            <div className="text-sm font-medium text-white">{result.enrichment.content_style}</div>
                          </div>
                        )}
                        {result.enrichment?.main_niche && (
                          <div className="p-2 bg-white/[0.06] rounded-lg">
                            <div className="text-xs text-white/40">Niche</div>
                            <div className="text-sm font-medium text-white">{result.enrichment.main_niche}</div>
                          </div>
                        )}
                        {result.enrichment?.total_collabs > 0 && (
                          <div className="p-2 bg-blue-500/10 rounded-lg">
                            <div className="text-xs text-white/40">Collaborations</div>
                            <div className="text-sm font-medium text-blue-300">{result.enrichment.total_collabs}</div>
                          </div>
                        )}
                        {result.enrichment?.sponsored_ratio >= 0 && (
                          <div className="p-2 bg-green-500/10 rounded-lg">
                            <div className="text-xs text-white/40">Authenticité</div>
                            <div className="text-sm font-medium text-green-300">{result.enrichment.sponsored_ratio}%</div>
                          </div>
                        )}
                        {result.enrichment?.recency_score >= 0 && (
                          <div className="p-2 bg-purple-500/10 rounded-lg">
                            <div className="text-xs text-white/40">Activité récente</div>
                            <div className="text-sm font-medium text-purple-300">{result.enrichment.recency_score}%</div>
                          </div>
                        )}
                      </div>

                      {/* Score Breakdown */}
                      <div className="mb-4 p-3 bg-white/[0.06] rounded-lg border border-white/[0.08]">
                        <div className="text-xs font-semibold text-white/60 mb-2">
                          Score détaillé: {result.score_breakdown.base_score} + {result.score_breakdown.bonus_score} bonus = {result.score}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-white/40">Brand Universe</span>
                            <span className="font-semibold text-white">{result.score_breakdown.brand_universe}/50</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/40">Persona</span>
                            <span className="font-semibold text-white">{result.score_breakdown.persona_total}/50</span>
                          </div>
                          {result.score_breakdown.content_style > 0 && (
                            <div className="flex justify-between">
                              <span className="text-white/40">Style Contenu</span>
                              <span className="font-semibold text-blue-300">+{result.score_breakdown.content_style}</span>
                            </div>
                          )}
                          {result.score_breakdown.performance > 0 && (
                            <div className="flex justify-between">
                              <span className="text-white/40">Performance</span>
                              <span className="font-semibold text-green-300">+{result.score_breakdown.performance}</span>
                            </div>
                          )}
                          {result.score_breakdown.discovery_bonus > 0 && (
                            <div className="flex justify-between col-span-2">
                              <span className="text-white/40">💎 Discovery Bonus</span>
                              <span className="font-semibold text-purple-300">+{result.score_breakdown.discovery_bonus}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Performance Highlights */}
                      {result.reasons?.performance_highlights && (
                        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                          <div className="text-xs font-semibold text-green-300 mb-1">✨ Points forts</div>
                          <div className="text-xs text-green-300/70">
                            {result.reasons.performance_highlights}
                          </div>
                        </div>
                      )}

                      {/* AI Senior TM Analysis */}
                      {result.tm_analysis && (
                        <div className="mb-4 p-4 bg-white/[0.06] border border-white/[0.08] rounded-xl">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-white/60" />
                            <div className="text-sm font-bold text-white">Analyse Senior Talent Manager</div>
                            <div className="ml-auto px-2 py-1 bg-white/[0.08] text-white text-xs font-bold rounded-full">
                              {result.tm_analysis.collaboration_potential}/10
                            </div>
                          </div>
                          <div className="space-y-3 text-sm">
                            <div>
                              <span className="font-semibold text-white">Pourquoi parfait:</span>
                              <div className="text-white/60 mt-1">{result.tm_analysis.why_perfect}</div>
                            </div>
                            <div>
                              <span className="font-semibold text-white">Atout principal:</span>
                              <div className="text-white/60 mt-1">{result.tm_analysis.key_strength}</div>
                            </div>
                            {result.tm_analysis.content_idea && (
                              <div className="pt-2 border-t border-white/[0.06]">
                                <span className="font-semibold text-white">💡 Idée de contenu:</span>
                                <div className="text-white/50 mt-1 italic bg-white/[0.04] p-2 rounded-lg">
                                  {result.tm_analysis.content_idea}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Reasons */}
                      <div className="space-y-2 mb-4">
                        {result.reasons?.brand_universe && (
                          <div className="text-sm text-white/50">
                            <span className="font-medium text-white">🎯 Brand:</span> {result.reasons.brand_universe}
                            {result.reasons.brand_universe_detail && ` - ${result.reasons.brand_universe_detail}`}
                          </div>
                        )}
                        {result.reasons?.persona_fit && (
                          <div className="text-sm text-white/50">
                            <span className="font-medium text-white">👤 Persona:</span> {result.reasons.persona_fit}
                            {result.reasons.persona_fit_detail && ` - ${result.reasons.persona_fit_detail}`}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            setSelectedCreator(result);
                            setIsCastingModalOpen(true);
                          }}
                          className="px-4 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-colors flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Ajouter au casting
                        </button>
                        <button
                          onClick={() => window.open(`https://instagram.com/${result.username}`, '_blank')}
                          className="px-4 py-2 bg-white/[0.08] text-white/60 rounded-xl border border-white/[0.08] hover:bg-white/[0.14] transition-colors"
                        >
                          Voir le profil
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Excluded Profiles Section */}
        {excludedByAI.length > 0 && (
          <div className="mt-8">
            <button
              onClick={() => setShowExcluded(!showExcluded)}
              className="w-full flex items-center justify-between px-6 py-4 bg-white/[0.04] hover:bg-white/[0.06] border border-white/[0.08] rounded-xl transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <X className="w-5 h-5 text-red-400" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-white">
                    Profils écartés par l'IA Senior TM
                  </div>
                  <div className="text-xs text-white/50">
                    {excludedByAI.length} profil{excludedByAI.length > 1 ? 's' : ''} ne correspond{excludedByAI.length > 1 ? 'ent' : ''} pas parfaitement à la campagne
                  </div>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${showExcluded ? 'rotate-180' : ''}`} />
            </button>

            {showExcluded && (
              <div className="mt-4 space-y-3">
                {excludedByAI.map((result) => (
                  <div
                    key={result.influencer_id}
                    className="bg-white/[0.04] border border-red-500/20 rounded-xl p-4 opacity-75"
                  >
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <img
                        src={result.profile_image_url || `https://ui-avatars.com/api/?name=${result.username}&size=64`}
                        alt={result.display_name}
                        className="w-16 h-16 rounded-full object-cover"
                      />

                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div>
                            <h4 className="font-bold text-white">
                              {result.display_name}
                            </h4>
                            <p className="text-sm text-white/50">@{result.username}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getScoreColor(result.score)}`}>
                              {result.score}
                            </span>
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div className="text-center p-2 bg-white/[0.06] rounded-lg">
                            <div className="text-xs text-white/40">Followers</div>
                            <div className="text-sm font-bold text-white">
                              {result.followers_count >= 1000000
                                ? `${(result.followers_count / 1000000).toFixed(1)}M`
                                : result.followers_count >= 1000
                                ? `${(result.followers_count / 1000).toFixed(1)}k`
                                : result.followers_count}
                            </div>
                          </div>
                          <div className="text-center p-2 bg-white/[0.06] rounded-lg">
                            <div className="text-xs text-white/40">Engagement</div>
                            <div className="text-sm font-bold text-white">
                              {result.engagement_rate?.toFixed(2)}%
                            </div>
                          </div>
                          <div className="text-center p-2 bg-white/[0.06] rounded-lg">
                            <div className="text-xs text-white/40">Collabs</div>
                            <div className="text-sm font-bold text-white">
                              {result.enrichment?.total_collabs || 0}
                            </div>
                          </div>
                        </div>

                        {/* AI Exclusion Reason */}
                        {result.tm_exclusion && (
                          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <div className="flex items-start gap-2">
                              <X className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                              <div>
                                <div className="text-xs font-semibold text-red-300 mb-1">Raison de l'exclusion</div>
                                <div className="text-xs text-red-300/70">{result.tm_exclusion.reason}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && results.length === 0 && !error && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/[0.06] flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-white/30" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Commencez votre recherche
            </h3>
            <p className="text-white/50 max-w-md mx-auto">
              Décrivez votre casting en langage naturel et laissez l'IA trouver les créateurs parfaits pour votre marque
            </p>
          </div>
        )}
      </div>

      {/* Add to Casting Modal */}
      {selectedCreator && (
        <AddToCastingModal
          isOpen={isCastingModalOpen}
          onClose={() => {
            setIsCastingModalOpen(false);
            setSelectedCreator(null);
          }}
          influencerId={selectedCreator.influencer_id}
          influencerData={{
            username: selectedCreator.username,
            display_name: selectedCreator.display_name,
            profile_image_url: selectedCreator.profile_image_url,
            followers_count: selectedCreator.followers_count,
            engagement_rate: selectedCreator.engagement_rate,
            category: selectedCreator.category
          }}
        />
      )}
    </div>
  );
};

// ─── BriefRecapCard ───────────────────────────────────────────────────────────

const ROSTER_OPTIONS: { value: BriefJson['roster_strategy']; label: string; desc: string; color: string }[] = [
  { value: 'renewal', label: 'Renouvellement', desc: 'Privilégier nouveaux créateurs', color: 'blue' },
  { value: 'consolidation', label: 'Consolidation', desc: 'Privilégier créateurs déjà actifs', color: 'emerald' },
  { value: 'mix', label: 'Mix équilibré', desc: '50/50', color: 'amber' },
];

interface BriefRecapCardProps {
  brief: BriefJson;
  onChange: (b: BriefJson) => void;
  onReset: () => void;
  onLaunch: () => void;
  isLoading: boolean;
  error: string | null;
}

const BriefRecapCard: React.FC<BriefRecapCardProps> = ({ brief, onChange, onReset, onLaunch, isLoading, error }) => {
  const set = (field: keyof BriefJson, value: any) => onChange({ ...brief, [field]: value });

  return (
    <div className="bg-[#141414] rounded-2xl border border-white/[0.08] overflow-hidden">
      {/* Header */}
      <div className="bg-white/[0.06] px-6 py-4 flex items-center justify-between border-b border-white/[0.08]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/[0.08] flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-base">Brief analysé</div>
            <div className="text-white/50 text-xs">Vérifiez et ajustez avant le matching</div>
          </div>
        </div>
        <button onClick={onReset} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.08] hover:bg-white/[0.14] text-white/60 text-xs rounded-xl border border-white/[0.08] transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
          Nouveau brief
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* Basic info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1.5">Marque *</label>
            <input value={brief.brand_name} onChange={e => set('brand_name', e.target.value)} className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm font-semibold" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1.5">Objectif campagne</label>
            <select value={brief.campaign_objective || ''} onChange={e => set('campaign_objective', e.target.value || null)} className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm">
              <option value="">Non précisé</option>
              {['notoriété', 'conversion', 'lancement', 'recrutement', 'autre'].map(v => <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1.5">Produit / focus</label>
            <input value={brief.product_focus || ''} onChange={e => set('product_focus', e.target.value || null)} className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm" placeholder="Ex: nouvelle ligne soin visage" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1.5">Budget</label>
            <input value={brief.budget_range || ''} onChange={e => set('budget_range', e.target.value || null)} className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm" placeholder="Ex: 5 000 - 15 000€" />
          </div>
        </div>

        {/* Audience */}
        <div>
          <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">Audience cible</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={brief.target_audience.age_range || ''}
              onChange={e => set('target_audience', { ...brief.target_audience, age_range: e.target.value || null })}
              className="px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm"
            >
              <option value="">Tranche d'âge</option>
              <option value="13-17">13 - 17 ans</option>
              <option value="18-24">18 - 24 ans</option>
              <option value="25-34">25 - 34 ans</option>
              <option value="35-44">35 - 44 ans</option>
              <option value="45-54">45 - 54 ans</option>
              <option value="55+">55 ans et +</option>
              <option value="18-34">18 - 34 ans</option>
              <option value="25-44">25 - 44 ans</option>
              <option value="18-44">18 - 44 ans</option>
            </select>
            <input value={brief.target_audience.geo || ''} onChange={e => set('target_audience', { ...brief.target_audience, geo: e.target.value || null })} className="px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm" placeholder="Géographie" />
            <input value={brief.target_audience.purchasing_power || ''} onChange={e => set('target_audience', { ...brief.target_audience, purchasing_power: e.target.value || null })} className="px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm" placeholder="Pouvoir d'achat" />
          </div>
        </div>

        {/* Creator profile */}
        <div>
          <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">Profil créateur</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-white/40 mb-1">Univers (virgule-séparés)</label>
              <input
                value={(brief.creator_profile.universes || []).join(', ')}
                onChange={e => set('creator_profile', { ...brief.creator_profile, universes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm"
                placeholder="beauté, lifestyle, sport"
              />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Mots-clés de ton</label>
              <input
                value={(brief.creator_profile.tone_keywords || []).join(', ')}
                onChange={e => set('creator_profile', { ...brief.creator_profile, tone_keywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm"
                placeholder="authentique, premium, fun"
              />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Followers min</label>
              <input
                type="number"
                value={brief.creator_profile.follower_range?.[0] || ''}
                onChange={e => {
                  const min = Number(e.target.value) || 0;
                  const max = brief.creator_profile.follower_range?.[1] ?? null;
                  set('creator_profile', { ...brief.creator_profile, follower_range: min > 0 || max ? [min, max ?? 10000000] : null });
                }}
                className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm"
                placeholder="Ex: 10 000"
              />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Followers max</label>
              <input
                type="number"
                value={brief.creator_profile.follower_range?.[1] && brief.creator_profile.follower_range[1] < 10000000 ? brief.creator_profile.follower_range[1] : ''}
                onChange={e => {
                  const max = Number(e.target.value) || 10000000;
                  const min = brief.creator_profile.follower_range?.[0] ?? 0;
                  set('creator_profile', { ...brief.creator_profile, follower_range: min > 0 || Number(e.target.value) > 0 ? [min, max] : null });
                }}
                className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm"
                placeholder="Ex: 500 000"
              />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Engagement min (%)</label>
              <input
                type="number" step="0.1"
                value={brief.creator_profile.min_engagement_rate || ''}
                onChange={e => set('creator_profile', { ...brief.creator_profile, min_engagement_rate: Number(e.target.value) || null })}
                className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm"
                placeholder="Ex: 2"
              />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Moyenne de vues par reel (min)</label>
              <input
                type="number"
                value={brief.creator_profile.min_avg_views || ''}
                onChange={e => set('creator_profile', { ...brief.creator_profile, min_avg_views: Number(e.target.value) || null })}
                className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm"
                placeholder="Ex: 50 000"
              />
            </div>
          </div>
        </div>

        {/* Roster strategy — prominent */}
        <div className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08]">
          <div className="flex items-center gap-2 mb-3">
            <RefreshCw className="w-4 h-4 text-white/60" />
            <span className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Stratégie Roster</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ROSTER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => set('roster_strategy', opt.value)}
                className={`p-3 rounded-xl border text-left transition-all ${
                  brief.roster_strategy === opt.value
                    ? 'border-white/30 bg-white/[0.08]'
                    : 'border-white/[0.08] bg-transparent hover:border-white/20'
                }`}
              >
                <div className={`flex items-center gap-2 mb-1 ${
                  brief.roster_strategy === opt.value ? 'text-white' : 'text-white/60'
                }`}>
                  <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                    brief.roster_strategy === opt.value ? 'border-white' : 'border-white/30'
                  }`}>
                    {brief.roster_strategy === opt.value && (
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="text-sm font-semibold">{opt.label}</span>
                </div>
                <div className="text-xs text-white/30 pl-5">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Exclusions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1.5">Marques exclues</label>
            <input
              value={(brief.excluded_brands || []).join(', ')}
              onChange={e => set('excluded_brands', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm"
              placeholder="Nike, Adidas..."
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1.5">Créateurs exclus</label>
            <input
              value={(brief.excluded_creators || []).join(', ')}
              onChange={e => set('excluded_creators', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none text-sm"
              placeholder="@username1, @username2..."
            />
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-sm text-red-400">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <button
          onClick={onLaunch}
          disabled={isLoading || !brief.brand_name}
          className="w-full py-4 bg-white text-black rounded-xl font-bold hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-base"
        >
          {isLoading ? (
            <><div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />Matching en cours...</>
          ) : (
            <><Target className="w-5 h-5" />Lancer le matching</>
          )}
        </button>
      </div>
    </div>
  );
};

// ─── BriefResultsSections ─────────────────────────────────────────────────────

interface BriefResultsSectionsProps {
  response: BriefMatchResponse;
  showDirect: boolean; setShowDirect: (v: boolean) => void;
  showLateral: boolean; setShowLateral: (v: boolean) => void;
  showDiscovery: boolean; setShowDiscovery: (v: boolean) => void;
  showExcluded: boolean; setShowExcluded: (v: boolean) => void;
  onAddToCasting: (creator: any) => void;
  onReset: () => void;
  getScoreColor: (s: number) => string;
  getScoreLabel: (s: number) => string;
}

const BriefResultsSections: React.FC<BriefResultsSectionsProps> = ({
  response, showDirect, setShowDirect, showLateral, setShowLateral,
  showDiscovery, setShowDiscovery, showExcluded, setShowExcluded,
  onAddToCasting, onReset, getScoreColor, getScoreLabel,
}) => {
  const { sections, excluded, brief_json, roster_strategy } = response;
  const isRenewal = roster_strategy === 'renewal';

  const sectionConfig = [
    {
      key: 'direct',
      icon: '🎯',
      title: 'Matchs directs',
      subtitle: `Créateurs ayant déjà collaboré avec ${brief_json?.brand_name || 'la marque'}`,
      data: sections.direct,
      show: showDirect,
      setShow: setShowDirect,
      headerClass: 'from-blue-600 to-blue-700',
      cardBorder: 'border-blue-100',
      warning: isRenewal ? `Stratégie de renouvellement active — ces profils sont rappelés à titre informatif uniquement` : null,
    },
    {
      key: 'lateral',
      icon: '🔄',
      title: 'Créateurs latéraux',
      subtitle: 'Ont travaillé avec des marques du Brand Universe — jamais avec la cible',
      data: sections.lateral,
      show: showLateral,
      setShow: setShowLateral,
      headerClass: 'from-emerald-600 to-emerald-700',
      cardBorder: 'border-emerald-100',
      warning: null,
      badge: 'Section phare',
    },
    {
      key: 'discovery',
      icon: '💎',
      title: 'Découvertes alignées',
      subtitle: 'Profils sans historique Brand Universe — persona fort & signaux de croissance',
      data: sections.discovery,
      show: showDiscovery,
      setShow: setShowDiscovery,
      headerClass: 'from-amber-500 to-amber-600',
      cardBorder: 'border-amber-100',
      warning: null,
    },
  ];

  const tmContext = brief_json?.brand_name
    ? `Sélection basée sur le brief ${brief_json.brand_name}${brief_json.campaign_objective ? ` (${brief_json.campaign_objective})` : ''} — Stratégie: ${
        roster_strategy === 'renewal' ? 'Renouvellement' : roster_strategy === 'consolidation' ? 'Consolidation' : 'Mix équilibré'
      }${brief_json.creator_profile?.universes?.length ? ` — Focus: ${brief_json.creator_profile.universes.join(', ')}` : ''}`
    : null;

  return (
    <div className="space-y-6">
      {/* Summary bar */}
      <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{sections.direct.length}</div>
            <div className="text-xs text-white/40">Directs</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{sections.lateral.length}</div>
            <div className="text-xs text-white/40">Latéraux</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-400">{sections.discovery.length}</div>
            <div className="text-xs text-white/40">Découvertes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-400">{excluded.length}</div>
            <div className="text-xs text-white/40">Écartés</div>
          </div>
        </div>
        <button onClick={onReset} className="flex items-center gap-2 px-4 py-2 bg-white/[0.08] hover:bg-white/[0.14] text-white/60 text-sm font-semibold rounded-xl border border-white/[0.08] transition-colors">
          <RefreshCw className="w-4 h-4" />
          Nouveau matching
        </button>
      </div>

      {/* TM context banner */}
      {tmContext && (
        <div className="bg-white/[0.06] border border-white/[0.08] rounded-xl px-5 py-3 flex items-center gap-3">
          <Sparkles className="w-4 h-4 text-white/60 flex-shrink-0" />
          <div className="text-sm text-white/60 font-medium">{tmContext}</div>
        </div>
      )}

      {/* Sections */}
      {sectionConfig.map(section => (
        <div key={section.key} className="bg-white/[0.04] rounded-2xl border border-white/[0.08] overflow-hidden">
          <button
            onClick={() => section.setShow(!section.show)}
            className={`w-full flex items-center justify-between px-6 py-4 bg-gradient-to-r ${section.headerClass} text-white hover:opacity-95 transition-opacity`}
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{section.icon}</span>
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-base">{section.title}</span>
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-semibold">{section.data.length}</span>
                  {section.badge && (
                    <span className="px-2 py-0.5 bg-white/30 border border-white/40 rounded-full text-xs font-bold">
                      {section.badge}
                    </span>
                  )}
                </div>
                <div className="text-white/80 text-xs mt-0.5">{section.subtitle}</div>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 transition-transform ${section.show ? 'rotate-180' : ''}`} />
          </button>

          {section.show && (
            <div className="p-4 space-y-3">
              {section.warning && (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-xs text-amber-300">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-400" />
                  {section.warning}
                </div>
              )}
              {section.data.length === 0 ? (
                <div className="py-8 text-center text-white/30 text-sm">Aucun profil dans cette catégorie</div>
              ) : (
                section.data.map((result: any) => (
                  <BriefCreatorCard
                    key={result.influencer_id}
                    result={result}
                    getScoreColor={getScoreColor}
                    getScoreLabel={getScoreLabel}
                    onAddToCasting={onAddToCasting}
                    borderClass={section.cardBorder}
                  />
                ))
              )}
            </div>
          )}
        </div>
      ))}

      {/* Excluded */}
      {excluded.length > 0 && (
        <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] overflow-hidden">
          <button
            onClick={() => setShowExcluded(!showExcluded)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/[0.04] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-red-500/20 flex items-center justify-center">
                <X className="w-4 h-4 text-red-400" />
              </div>
              <div className="text-left">
                <div className="font-bold text-white">Profils non retenus</div>
                <div className="text-xs text-white/50">{excluded.length} profil{excluded.length > 1 ? 's' : ''} écarté{excluded.length > 1 ? 's' : ''} avec raison contextuelle</div>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${showExcluded ? 'rotate-180' : ''}`} />
          </button>
          {showExcluded && (
            <div className="p-4 space-y-3">
              {excluded.map((result: any) => (
                <div key={result.influencer_id} className="flex items-start gap-4 p-4 bg-white/[0.04] rounded-xl border border-white/[0.08] opacity-70">
                  <img src={result.profile_image_url || `https://ui-avatars.com/api/?name=${result.username}&size=48`} alt={result.username} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <div className="font-semibold text-white text-sm">{result.display_name}</div>
                        <div className="text-xs text-white/50">@{result.username}</div>
                      </div>
                      <span className={`px-2 py-1 rounded-lg text-xs font-bold border ${getScoreColor(result.score)}`}>{result.score}</span>
                    </div>
                    {result.tm_exclusion?.reason && (
                      <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                        <X className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-red-300/70">{result.tm_exclusion.reason}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── BriefCreatorCard ─────────────────────────────────────────────────────────

interface BriefCreatorCardProps {
  result: any;
  getScoreColor: (s: number) => string;
  getScoreLabel: (s: number) => string;
  onAddToCasting: (r: any) => void;
  borderClass: string;
}

const BriefCreatorCard: React.FC<BriefCreatorCardProps> = ({ result, getScoreColor, getScoreLabel, onAddToCasting, borderClass }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white/[0.04] rounded-xl border border-white/[0.08] hover:bg-white/[0.06] transition-colors">
      <div className="p-4">
        <div className="flex items-start gap-4">
          <img
            src={result.profile_image_url || `https://ui-avatars.com/api/?name=${result.username}&size=64`}
            alt={result.username}
            className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="font-bold text-white text-base">{result.display_name || result.username}</div>
                <div className="text-sm text-white/50">@{result.username}</div>
              </div>
              <div className={`px-3 py-1.5 rounded-xl border-2 text-center flex-shrink-0 ${getScoreColor(result.score)}`}>
                <div className="text-lg font-bold leading-none">{result.score}</div>
                <div className="text-xs leading-none mt-0.5">{getScoreLabel(result.score)}</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 text-sm text-white/50 mb-3">
              <span><strong className="text-white">{(result.followers_count || 0).toLocaleString()}</strong> followers</span>
              {result.engagement_rate && <span><strong className="text-white">{result.engagement_rate.toFixed(2)}%</strong> eng.</span>}
              {result.category && <span className="px-2 py-0.5 bg-white/[0.08] rounded-full text-xs text-white/60">{result.category}</span>}
            </div>

            {/* Score breakdown compact */}
            <div className="flex flex-wrap gap-2 mb-3 text-xs">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-lg font-medium">Brand {result.score_breakdown?.brand_universe || 0}pts</span>
              <span className="px-2 py-1 bg-white/[0.08] text-white/60 rounded-lg font-medium">Persona {result.score_breakdown?.persona_total || 0}pts</span>
              {(result.score_breakdown?.performance || 0) > 0 && <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-lg font-medium">+{result.score_breakdown.performance} perf</span>}
              {(result.score_breakdown?.discovery_bonus || 0) > 0 && <span className="px-2 py-1 bg-amber-500/20 text-amber-300 rounded-lg font-medium">+{result.score_breakdown.discovery_bonus} discovery</span>}
            </div>

            {/* Matched brands */}
            {result.matched_brands?.length > 0 && (
              <div className="mb-3 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg text-xs text-blue-300">
                <span className="font-semibold">Marques travaillées :</span> {result.matched_brands.slice(0, 6).join(', ')}{result.matched_brands.length > 6 ? ` +${result.matched_brands.length - 6}` : ''}
              </div>
            )}

            {/* TM analysis */}
            {result.tm_analysis && (
              <div className="mb-3 p-3 bg-white/[0.06] border border-white/[0.08] rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-white/60" />
                  <span className="text-xs font-bold text-white">Analyse Senior TM</span>
                  {result.tm_analysis.collaboration_potential && (
                    <span className="ml-auto px-2 py-0.5 bg-white/[0.08] text-white text-xs font-bold rounded-full">{result.tm_analysis.collaboration_potential}/10</span>
                  )}
                </div>
                <div className="text-xs text-white/50 space-y-1">
                  {result.tm_analysis.why_perfect && <div><strong className="text-white/70">Pourquoi :</strong> {result.tm_analysis.why_perfect}</div>}
                  {result.tm_analysis.strategy_fit && <div className="italic text-white/40">{result.tm_analysis.strategy_fit}</div>}
                </div>
                {result.tm_analysis.content_idea && expanded && (
                  <div className="mt-2 pt-2 border-t border-white/[0.06] text-xs text-white/50 italic bg-white/[0.04] px-2 py-1.5 rounded-lg">
                    {result.tm_analysis.content_idea}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => onAddToCasting(result)} className="px-3 py-2 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-colors flex items-center gap-1.5 text-sm">
                <Plus className="w-4 h-4" />
                Ajouter au casting
              </button>
              <button onClick={() => window.open(`https://instagram.com/${result.username}`, '_blank')} className="px-3 py-2 bg-white/[0.08] text-white/60 rounded-xl border border-white/[0.08] hover:bg-white/[0.14] transition-colors text-sm">
                Voir profil
              </button>
              {result.tm_analysis?.content_idea && (
                <button onClick={() => setExpanded(!expanded)} className="px-3 py-2 bg-white/[0.08] text-white/60 rounded-xl border border-white/[0.08] hover:bg-white/[0.14] transition-colors text-sm flex items-center gap-1">
                  <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                  Idée
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingIA;
